import { achievementCatalogue } from "./achievementCatalogue.mjs";
const en = {
  yourAchievement: "Your achievement",
  qualificationRecords: "Qualification & records",
  recordedActivity: "Recorded activity",
  progressOf: (value, target) => `${value} of ${target}`,
  exceptional: "Exceptional results",
  historicalPar:
    "Historical hole snapshots or saved hole scores relative to par are needed; today's course par is not used.",
  matchResult:
    "The result needs a supported final score and winner. Saved casual sessions, retirements and unfinished matches do not establish a completed match.",
  title: "Achievements",
  compactEarnedCount: (n) => `${new Intl.NumberFormat("en-GB").format(n)} earned`,
  allCategories: "All categories",
  informationLabel: "About achievement records",
  intro: "Your milestones from life at the club.",
  earnedCount: (n) => `${n} ${n === 1 ? "award" : "awards"} earned`,
  all: "All",
  earned: "Earned",
  in_progress: "In progress",
  history_incomplete: "Recorded history is limited",
  source_unavailable: "Source temporarily unavailable",
  not_applicable: "Not applicable",
  category: "Category",
  membership: "Membership",
  heritage: "Family heritage",
  golf: "Golf",
  racquet: "Racquet sports",
  events: "Club events",
  spa: "Spa",
  academy: "Academy",
  families: {
    membership: "Membership",
    heritage: "Family heritage",
    golf_rounds: "Golf rounds",
    racquet_matches: "Racquet matches",
    events: "Club events",
    spa: "Spa treatments",
    academy: "Academy programmes",
  },
  units: {
    continuous_years: ["year", "years"],
    heritage_years: ["year", "years"],
    rounds: ["round", "rounds"],
    matches: ["match", "matches"],
    events: ["event", "events"],
    treatments: ["treatment", "treatments"],
    programmes: ["programme", "programmes"],
  },
  sports: {
    tennis: "Tennis",
    padel: "Padel",
    squash: "Squash",
    pickleball: "Pickleball",
  },
  loading: "Loading achievements…",
  error: "Your achievements could not be loaded.",
  retry: "Try again",
  empty: "No achievements match these filters.",
  close: "Close",
  details: "Award details",
  tiers: "All family milestones",
  milestones: "Milestones",
  earnedDate: "Earned on",
  unknownDate: "The exact date is not known.",
  partial:
    "Progress reflects available records; earlier history may be missing.",
  pending: "Your records are being evaluated.",
  next: "Next",
  future: "Future",
  starting: "No earned tier yet",
  notEarned: "Not yet earned",
  complete: "All milestones earned",
  progressUnknown: "Progress unavailable",
  source: "Source",
  sources: {
    membership: "From your membership dates and history",
    heritage: "From your family links and membership history",
    golf: "From your Let’s Play results",
    racquet: "From your Let’s Play results",
    events: "From your past event bookings",
    spa: "From your past spa bookings",
    academy: "From your learner enrolments and learning results",
  },
  bookingPolicy:
    "Scheduled bookings count from the next calendar day after their final scheduled date, in the club’s timezone. Cancelled bookings and no-shows do not count.",
  heritagePolicy:
    "Existing eligible family links connect known membership periods, including ancestors through parent/child links. Undated links use the existing family relationship; overlapping periods count once.",
  automaticHelp:
    "Awards update automatically. Correct membership dates, family links, bookings and scores in their original workflows.",
  history: "Achievements / History",
  audit: "Correction and issuance history",
  reconcile: "Request reconciliation",
  saved: "Reconciliation is queued.",
  staffError: "The request could not be completed. Try again.",
  more: "Load more history",
  qualification: (d) =>
    ({
      continuous_years: `Reach ${d.threshold} ${d.threshold === 1 ? "year" : "years"} of continuous membership.`,
      heritage_years: `Reach ${d.threshold} years of family membership, counting overlapping periods once.`,
      rounds: `Complete ${d.threshold} recorded golf ${d.threshold === 1 ? "round" : "rounds"}.`,
      ace: "Record a valid played hole in one stroke.",
      under_par: "Complete a gross round below its recorded historical par.",
      matches: `Complete ${d.threshold} racquet ${d.threshold === 1 ? "match" : "matches"}.`,
      perfect:
        "Win a completed supported tennis or padel match without conceding a game.",
      events: `Have ${d.threshold} eligible past event ${d.threshold === 1 ? "booking" : "bookings"}.`,
      treatments: `Have ${d.threshold} eligible past spa ${d.threshold === 1 ? "treatment" : "treatments"}.`,
      programmes: `Finish ${d.threshold} Academy ${d.threshold === 1 ? "programme" : "programmes"} through scheduled enrolments or recorded online completion.`,
    })[d.unit],
};
const es = {
  yourAchievement: "Tu logro",
  qualificationRecords: "Requisitos y registros",
  recordedActivity: "Actividad registrada",
  progressOf: (value, target) => `${value} de ${target}`,
  exceptional: "Resultados excepcionales",
  historicalPar:
    "Se necesitan datos históricos de los hoyos o resultados guardados respecto al par; no se usa el par actual del campo.",
  matchResult:
    "El resultado necesita un marcador final compatible y un ganador. Las sesiones informales guardadas, las retiradas y los partidos sin terminar no acreditan un partido completo.",
  title: "Logros",
  compactEarnedCount: (n) => `${new Intl.NumberFormat("es").format(n)} ${n === 1 ? "conseguido" : "conseguidos"}`,
  allCategories: "Todas las categorías",
  informationLabel: "Información sobre los registros de logros",
  intro: "Tus hitos de la vida en el club.",
  earnedCount: (n) =>
    `${n} ${n === 1 ? "distintivo conseguido" : "distintivos conseguidos"}`,
  all: "Todos",
  earned: "Conseguido",
  in_progress: "En progreso",
  history_incomplete: "El historial registrado es limitado",
  source_unavailable: "Fuente temporalmente no disponible",
  not_applicable: "No aplicable",
  category: "Categoría",
  membership: "Pertenencia",
  heritage: "Legado familiar",
  golf: "Golf",
  racquet: "Deportes de raqueta",
  events: "Eventos del club",
  spa: "Spa",
  academy: "Academia",
  families: {
    membership: "Pertenencia",
    heritage: "Legado familiar",
    golf_rounds: "Vueltas de golf",
    racquet_matches: "Partidos de raqueta",
    events: "Eventos del club",
    spa: "Tratamientos de spa",
    academy: "Programas de la Academia",
  },
  units: {
    continuous_years: ["año", "años"],
    heritage_years: ["año", "años"],
    rounds: ["vuelta", "vueltas"],
    matches: ["partido", "partidos"],
    events: ["evento", "eventos"],
    treatments: ["tratamiento", "tratamientos"],
    programmes: ["programa", "programas"],
  },
  sports: {
    tennis: "Tenis",
    padel: "Pádel",
    squash: "Squash",
    pickleball: "Pickleball",
  },
  loading: "Cargando logros…",
  error: "No se pudieron cargar tus logros.",
  retry: "Reintentar",
  empty: "Ningún logro coincide con estos filtros.",
  close: "Cerrar",
  details: "Detalles del distintivo",
  tiers: "Todos los hitos de la familia",
  milestones: "Hitos",
  earnedDate: "Conseguido el",
  unknownDate: "No se conoce la fecha exacta.",
  partial:
    "El progreso refleja los registros disponibles; puede faltar historial anterior.",
  pending: "Se están evaluando tus registros.",
  next: "Siguiente",
  future: "Futuro",
  starting: "Aún no hay un nivel conseguido",
  notEarned: "Aún no conseguido",
  complete: "Todos los hitos conseguidos",
  progressUnknown: "Progreso no disponible",
  source: "Origen",
  sources: {
    membership: "De tus fechas e historial de pertenencia",
    heritage: "De tus vínculos familiares e historial de pertenencia",
    golf: "De tus resultados de Let’s Play",
    racquet: "De tus resultados de Let’s Play",
    events: "De tus reservas de eventos pasados",
    spa: "De tus reservas de spa pasadas",
    academy: "De tus inscripciones y resultados de aprendizaje",
  },
  bookingPolicy:
    "Las reservas programadas cuentan desde el día siguiente a su última fecha programada, en la zona horaria del club. No cuentan las cancelaciones ni las ausencias registradas.",
  heritagePolicy:
    "Los vínculos familiares elegibles conectan períodos de pertenencia conocidos, incluidos antepasados mediante vínculos de progenitores e hijos. Los vínculos sin fecha usan la relación existente; los períodos solapados cuentan una sola vez.",
  automaticHelp:
    "Los distintivos se actualizan automáticamente. Corrige fechas de pertenencia, vínculos familiares, reservas y resultados en sus secciones de origen.",
  history: "Logros / Historial",
  audit: "Historial de correcciones y concesiones",
  reconcile: "Solicitar conciliación",
  saved: "La conciliación está en cola.",
  staffError: "No se pudo completar la solicitud. Reintenta.",
  more: "Cargar más historial",
  qualification: (d) =>
    ({
      continuous_years: `Alcanzar ${d.threshold} ${d.threshold === 1 ? "año" : "años"} de pertenencia continua.`,
      heritage_years: `Alcanzar ${d.threshold} años de pertenencia familiar, contando una vez los períodos solapados.`,
      rounds: `Completar ${d.threshold} ${d.threshold === 1 ? "vuelta registrada" : "vueltas registradas"} de golf.`,
      ace: "Registrar un hoyo válido jugado en un solo golpe.",
      under_par:
        "Completar una vuelta bruta por debajo de su par histórico registrado.",
      matches: `Completar ${d.threshold} ${d.threshold === 1 ? "partido" : "partidos"} de raqueta.`,
      perfect:
        "Ganar un partido completo de tenis o pádel con un formato compatible sin ceder ningún juego.",
      events: `Tener ${d.threshold} ${d.threshold === 1 ? "reserva elegible" : "reservas elegibles"} de eventos pasados.`,
      treatments: `Tener ${d.threshold} ${d.threshold === 1 ? "tratamiento elegible" : "tratamientos elegibles"} de spa pasados.`,
      programmes: `Finalizar ${d.threshold} ${d.threshold === 1 ? "programa" : "programas"} de la Academia mediante inscripciones programadas o finalización en línea registrada.`,
    })[d.unit],
};
export const achievementText = (locale) =>
  String(locale).startsWith("es") ? es : en;
export const achievementName = (d, locale) =>
  d.name[String(locale).startsWith("es") ? "es" : "en-GB"];
export const achievementMessages = (locale) => {
  const t = achievementText(locale);
  return {
    ...Object.fromEntries(
      Object.entries(t).filter(([, v]) => typeof v === "string"),
    ),
    awards: Object.fromEntries(
      achievementCatalogue.map((d) => [d.code, achievementName(d, locale)]),
    ),
    qualifications: Object.fromEntries(
      achievementCatalogue.map((d) => [d.code, t.qualification(d)]),
    ),
  };
};
